"use client"

import dynamic from "next/dynamic"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { RouteMapCloseButton } from "./RouteMapCloseButton"

// O mapa é pesado (Leaflet) e não pode entrar no bundle principal: o mesmo
// dynamic(ssr:false) do /trip garante que ele só seja baixado quando o dialog
// abrir. Sem SSR, o Leaflet não toca `window` no meio da renderização do servidor.
const RideRouteMap = dynamic(
  () => import("@/components/RideRouteMap").then((m) => m.RideRouteMap),
  { ssr: false, loading: () => <Skeleton className="h-full w-full rounded-none" /> }
)

// Mapa da rota da carona em tela cheia, com o trecho do passageiro destacado
// sobre a rota. Vive separado do mapa embutido (card de 300px) porque o card
// só aparece quando a busca casou a carona com um ponto FORA da rota
// (deslocamento > 1 km) — neste caso o passageiro ainda quer ver por onde passa
// o seu trecho, e o mapa cheio é a única forma.
//
// É um Dialog do Radix com o conteúdo estourando para a tela toda. O X padrão do
// Radix ficaria no canto do `DialogContent` (que aqui é a tela inteira), então
// ele é escondido e trocado por um botão flutuante sobre o mapa — mesma posição
// visual, mas com fundo próprio para continuar legível sobre os tiles.
interface RouteMapDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  origin?: { name?: string; lat?: number; lng?: number }
  destination?: { name?: string; lat?: number; lng?: number }
  stops?: Array<{ name: string; lat?: number; lng?: number; price?: number }>
  overviewPolyline?: string
  /**
   * Trecho do passageiro sobre a rota, em azul mais forte. Ausente (dono sem
   * busca), o mapa mostra só a rota da carona com origem/destino/paradas.
   */
  passengerSegment?: { points?: Array<{ lat: number; lng: number }>; color?: string }
  boardPoint?: { lat: number; lng: number; name?: string }
  alightPoint?: { lat: number; lng: number; name?: string }
  showOriginDisplacement?: boolean
  showDestDisplacement?: boolean
  searchOriginCoords?: { lat: number; lng: number }
  searchDestCoords?: { lat: number; lng: number }
  /** Índices (no array `stops`) das paradas dentro do trecho — esmaecidas. */
  dimStopIndexes?: number[]
  /**
   * Pontos de embarque/desembarque de reservas que não são origem/destino/
   * parada da carona (ex.: descer em Medianeira numa carona Foz → Campo Mourão
   * cuja única parada é Cascavel). Ausente, nada muda.
   */
  reservationPoints?: Array<{ lat: number; lng: number; label: string }>
}

export function RouteMapDialog({
  open,
  onOpenChange,
  origin,
  destination,
  stops,
  overviewPolyline,
  passengerSegment,
  boardPoint,
  alightPoint,
  showOriginDisplacement,
  showDestDisplacement,
  searchOriginCoords,
  searchDestCoords,
  dimStopIndexes,
  reservationPoints,
}: RouteMapDialogProps) {
  return (
    <>
      <RouteMapCloseButton open={open} onClose={() => onOpenChange(false)} />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-h-none w-screen max-w-none gap-0 rounded-none border-0 p-0 sm:rounded-none [&>button.ring-offset-background]:hidden"
          aria-describedby={undefined}
        >
          {/* Título só para o Radix (leitor de tela); o cabeçalho é o próprio mapa. */}
          <DialogTitle className="sr-only">Rota da carona</DialogTitle>

          {/* O mapa ocupa a tela toda. `100dvh` é altura literal na prop do mapa
              (percentual pode nascer 0×0 — ver StopPointRefineDialog). */}
          <div className="h-[100dvh] w-full overflow-hidden">
            <RideRouteMap
              height="100dvh"
              origin={origin}
              destination={destination}
              stops={stops}
              overviewPolyline={overviewPolyline}
              passengerSegment={passengerSegment}
              boardPoint={boardPoint}
              alightPoint={alightPoint}
              showBoardPin={!!boardPoint}
              showAlightPin={!!alightPoint}
              dimStopIndexes={dimStopIndexes}
              showOriginDisplacement={showOriginDisplacement}
              showDestDisplacement={showDestDisplacement}
              searchOriginCoords={searchOriginCoords}
              searchDestCoords={searchDestCoords}
              reservationPoints={reservationPoints}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
